import { AxiosError } from 'axios';

import { getApiErrorMessage } from './getApiErrorMessage';

export type EmailVerificationErrorAction = 'resend' | 'retry';

export type EmailVerificationErrorState = {
  severity: 'error' | 'info';
  message: string;
  actionTitle?: string;
  action?: EmailVerificationErrorAction;
};

/**
 * Checks if an axios error is a network error (offline, timeout, etc.)
 */
function isNetworkError(error: AxiosError): boolean {
  return !error.response && (error.code === 'ERR_NETWORK' || error.message.includes('Network Error'));
}

/**
 * Maps email verification API errors to user-friendly error states with appropriate actions.
 */
export function getEmailVerificationError(err: unknown): EmailVerificationErrorState {
  const axiosError = err as AxiosError<{ message?: string }>;

  // Check for network errors (no response from server)
  if (isNetworkError(axiosError)) {
    return {
      severity: 'error',
      message: 'Unable to connect to the server. Please check your internet connection and try again.',
      actionTitle: 'Retry',
      action: 'retry',
    };
  }

  const status = axiosError.response?.status;

  // Map status codes to error states
  switch (status) {
    case 401:
    case 404:
      // Invalid or already used link
      return {
        severity: 'error',
        message: 'This verification link is invalid or has already been used.',
        actionTitle: 'Request New Link',
        action: 'resend',
      };

    case 410:
      // Expired link
      return {
        severity: 'error',
        message: 'Your verification link has expired.',
        actionTitle: 'Resend Verification',
        action: 'resend',
      };

    default:
      // Generic error
      return {
        severity: 'error',
        message: getApiErrorMessage(err),
        actionTitle: 'Retry',
        action: 'retry',
      };
  }
}
